import { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, arrayRemove } from 'firebase/firestore';
import { Firestore } from 'firebase/firestore';
import { httpsCallable, type Functions } from 'firebase/functions';
import { Unit, UserRole, User } from '../types';

interface MemberProfileResult {
  sourceUid: string;
  uid: string;
  name: string;
  email: string;
  stripeCustomerId?: string;
  futureMeetingsOptIn: boolean;
  createdAt: string | null;
  lastLoginAt: string | null;
  twoFactorEnabled: boolean;
  hasAccount: boolean;
}

export function useUnitMembers(
  db: Firestore,
  functions: Functions | null | undefined,
  unit: Unit | null,
  activeTab: string,
  showSuccess: (message: string) => void,
  showError: (message: string) => void
) {
  const [members, setMembers] = useState<Array<User & { role: UserRole; joinedAt: Date; hasAccount?: boolean }>>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Helper function to check if a UID is an email-based identifier (member without account)
  const isEmailBasedMember = (uid: string | undefined): boolean => {
    if (!uid) return false;
    return uid.startsWith('email:') || (!uid.includes('-') && uid.includes('@'));
  };

  // Helper function to extract email from member identifier
  const getEmailFromMember = (member: { uid?: string; email?: string }): string => {
    if (member.email) {
      return member.email;
    }
    if (!member.uid) {
      return '';
    }
    if (member.uid.startsWith('email:')) {
      return member.uid.replace('email:', '');
    }
    if (member.uid.includes('@')) {
      return member.uid;
    }
    return '';
  };

  // Helper function to clean member objects - remove undefined fields (Firestore doesn't allow undefined)
  const cleanMember = (member: any): any | null => {
    // Generate uid if missing (for imported members without accounts)
    let memberUid = member.uid;
    if (!memberUid && member.email) {
      memberUid = `email:${member.email.toLowerCase()}`;
    } else if (!memberUid) {
      // No uid and no email - skip this invalid member (silently, as we log in fetchMembers)
      return null;
    }
    
    const cleaned: any = {
      uid: memberUid,
      role: member.role || 'Member',
      joinedAt: member.joinedAt || new Date(),
    };
    // Only add optional fields if they're defined (check for undefined/null, not falsy)
    // Empty strings are allowed in Firestore, but undefined is not
    if (member.email !== undefined && member.email !== null) {
      cleaned.email = member.email;
    }
    if (member.firstName !== undefined && member.firstName !== null) {
      cleaned.firstName = member.firstName;
    }
    if (member.lastName !== undefined && member.lastName !== null) {
      cleaned.lastName = member.lastName;
    }
    return cleaned;
  };

  // Helper function to clean members array before saving to Firestore
  const cleanMembersArray = (members: any[]): any[] => {
    return members.map(cleanMember).filter((m): m is any => m !== null);
  };

  useEffect(() => {
    const fetchMembers = async () => {
      if (!unit || activeTab !== 'members') return;

      setLoadingMembers(true);
      try {
        // The `users` collection only allows a doc's owner or an admin to read it,
        // so a regular unit member cannot look up their fellow members' profiles
        // directly from the client. Profile lookups run server-side instead.
        if (!functions) {
          showError('Member lookup service is not available. Please refresh and try again.');
          return;
        }

        const validMembers = unit.members.filter((member) => member.uid || member.email);
        const invalidCount = unit.members.length - validMembers.length;
        if (invalidCount > 0) {
          console.warn(
            `Skipped ${invalidCount} invalid member(s) without uid or email in unit ${unit.id}. ` +
            `These members should be removed or updated with valid email addresses.`
          );
        }

        const getMemberProfiles = httpsCallable<{ unitId: string }, { members: MemberProfileResult[] }>(
          functions,
          'getUnitMemberProfiles'
        );
        const result = await getMemberProfiles({ unitId: unit.id });
        const profilesBySourceUid = new Map(result.data.members.map((p) => [p.sourceUid, p]));

        const memberDetails: Array<User & { role: UserRole; joinedAt: Date; hasAccount?: boolean; firstName?: string; lastName?: string }> = [];

        for (const member of validMembers) {
          const memberUid = member.uid || `email:${getEmailFromMember(member).toLowerCase()}`;
          const profile = profilesBySourceUid.get(memberUid);
          const joinedAt =
            (member.joinedAt as any)?.toDate?.() ||
            (member.joinedAt instanceof Date ? member.joinedAt : new Date());

          if (profile) {
            memberDetails.push({
              uid: profile.uid,
              name: profile.name,
              email: profile.email,
              stripeCustomerId: profile.stripeCustomerId,
              futureMeetingsOptIn: profile.futureMeetingsOptIn,
              units: [],
              createdAt: profile.createdAt ? new Date(profile.createdAt) : new Date(),
              lastLoginAt: profile.lastLoginAt ? new Date(profile.lastLoginAt) : new Date(),
              twoFactorEnabled: profile.twoFactorEnabled,
              role: member.role || 'Member',
              joinedAt,
              hasAccount: profile.hasAccount,
              firstName: member.firstName,
              lastName: member.lastName,
            });
          } else {
            // Server didn't return a match (shouldn't normally happen) - fall back to stored data.
            const memberEmail = getEmailFromMember(member);
            const fullName = member.firstName || member.lastName
              ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
              : (memberEmail ? memberEmail.split('@')[0] : 'Unknown User');

            memberDetails.push({
              uid: memberUid,
              name: fullName,
              email: memberEmail || memberUid,
              stripeCustomerId: undefined,
              futureMeetingsOptIn: false,
              units: [],
              createdAt: new Date(),
              lastLoginAt: new Date(),
              twoFactorEnabled: false,
              role: member.role || 'Member',
              joinedAt,
              hasAccount: false,
              firstName: member.firstName,
              lastName: member.lastName,
            });
          }
        }

        setMembers(memberDetails);
      } catch (error) {
        console.error('Error fetching members:', error);
        showError('Failed to load members');
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [unit, activeTab, db, functions, showError]);

  const handleChangeMemberRole = async (uid: string, newRole: UserRole) => {
    if (!unit) return;

    // Check if member has an account
    const member = members.find((m) => m.uid === uid);
    if (member && member.hasAccount === false) {
      showError('Cannot change role for members without accounts. They must create an account first.');
      return null;
    }

    try {
      // Update members array - preserve email/name fields for email-based members
      // Clean all members to remove undefined fields
      const updatedMembers = cleanMembersArray(unit.members).map((m) => {
        if (m.uid === uid) {
          return {
            ...m,
            role: newRole,
          };
        }
        return m;
      });

      await updateDoc(doc(db, 'units', unit.id), {
        members: updatedMembers,
      });

      // Update user's unit membership (only if they have an account)
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userUnits = userData.units || [];
        const updatedUserUnits = userUnits.map((l: any) =>
          l.unitId === unit.id ? { ...l, role: newRole } : l
        );

        await updateDoc(doc(db, 'users', uid), {
          units: updatedUserUnits,
        });
      }

      setMembers(members.map((m) => (m.uid === uid ? { ...m, role: newRole } : m)));
      showSuccess('Member role updated successfully');

      return updatedMembers;
    } catch (error) {
      console.error('Error changing member role:', error);
      showError('Failed to change member role');
      return null;
    }
  };

  const handleRemoveMember = async (uid: string, memberName: string) => {
    if (!unit) return false;

    try {
      // Clean existing members before filtering
      const cleanedMembers = cleanMembersArray(unit.members);
      const updatedMembers = cleanedMembers.filter((m) => m.uid !== uid);

      await updateDoc(doc(db, 'units', unit.id), {
        members: updatedMembers,
      });

      // Remove unit from user's profile (only if they have an account)
      // Check if this is a regular UID (not email-based)
      if (!isEmailBasedMember(uid)) {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userUnits = userData.units || [];
          const unitMembership = userUnits.find((l: any) => l.unitId === unit.id);

          if (unitMembership) {
            await updateDoc(doc(db, 'users', uid), {
              units: arrayRemove(unitMembership),
            });
          }
        }
      }

      setMembers(members.filter((m) => m.uid !== uid));
      showSuccess(`${memberName}'s access has been removed`);

      return updatedMembers;
    } catch (error) {
      console.error('Error removing member:', error);
      showError('Failed to remove member access');
      return null;
    }
  };

  const handleImportMembers = async (
    membersToImport: Array<{ firstName: string; lastName: string; email: string }>
  ): Promise<{ importId: string }> => {
    if (!unit) {
      throw new Error('Unit not found');
    }

    if (!functions) {
      showError('Member import service is not available. Please refresh and try again.');
      throw new Error('Import service unavailable');
    }

    try {
      const { httpsCallable } = await import('firebase/functions');
      const startUnitMemberImport = httpsCallable<
        {
          unitId: string;
          members: Array<{ firstName: string; lastName: string; email: string }>;
        },
        { importId: string }
      >(functions, 'startUnitMemberImport');

      const result = await startUnitMemberImport({
        unitId: unit.id,
        members: membersToImport,
      });

      if (!result.data?.importId) {
        throw new Error('Import could not be started');
      }

      return { importId: result.data.importId };
    } catch (error: unknown) {
      console.error('Error starting member import:', error);
      const code =
        error && typeof error === 'object' && 'code' in error
          ? String((error as { code?: string }).code)
          : '';
      const message = error instanceof Error ? error.message : 'Failed to start member import';

      if (code === 'functions/failed-precondition') {
        showError('Another member import is already running for this unit.');
      } else {
        showError(message);
      }

      throw error instanceof Error ? error : new Error(message);
    }
  };

  return {
    members,
    setMembers,
    loadingMembers,
    handleChangeMemberRole,
    handleRemoveMember,
    handleImportMembers,
  };
}

